import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { EMPLOYEENOTES_VIEW, EMPLOYEENOTES_ADD } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import { MCard, MSpinner, MSelect } from "components/Shared";
import { getEmployeeNotes, addEmployeeNote } from "requests/employee";
import NoteInputField from "components/Shared/NoteInputField";
import {
  formatNoteDateCreated,
  getTabbedHistoryTable,
  base64String,
  formatNote,
  filterOnFilterDate,
} from "components/Homeowner/History/DataFormatters";
import Box from "@mui/material/Box";

const EmployeeTEAM = ({
  internetUserId,
  cellWidth,
  canViewNotes,
  canAddNotes,
  hideAddNotes,
  isCollapsible,
  filterDate,
  overrideCellWidth,
}) => {
  const mounted = useRef(true);
  const [notes, setNotes] = useState([]);
  const [isFetchingNotes, setIsFetchingNotes] = useState(false);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // defined in EmployeeNoteCategories.cs
  const employeeNoteCategories = ["TEAM Entry", "Support Line"];

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  useEffect(() => {
    setIsFetchingNotes(true);
    setIsAddingNote(false);

    getEmployeeNotes(
      internetUserId,
      (notes) => {
        setNotes(notes || []);
        setIsFetchingNotes(false);
      },
      mounted,
    );
  }, [internetUserId]);

  const formatEmployeeNote = (noteHtml) => {
    // format noteHtml to drop html markup in case it is pasted from another source/display img properly in grid
    const div = document.createElement("div");
    div.innerHTML = noteHtml;
    let newNote = "";
    if (div.childNodes.length > 1 && (noteHtml.includes(base64String) || noteHtml.includes("<img"))) {
      // filter html with both text and/or screenshots
      div.childNodes.forEach((cn) => {
        if (cn.childNodes.length > 1) {
          // need to check childNodes multiple times when hitting enter while typing and pasting note in input or else multiple screenshots and paragraphs won"t save/be filtered properly
          cn.childNodes.forEach((n) => (newNote += formatNote(n, newNote)));
        }
        newNote += formatNote(cn, newNote);
      });
      return newNote;
    } else if (div.firstChild.tagName === "IMG")
      return div.firstChild.src; // filter html with only screenshot present
    else return noteHtml.replace(/<[^>]+>/g, " "); // filter html with only text present
  };

  const addNote = (noteValue) => {
    if (!canAddNotes || hideAddNotes) return;
    setIsAddingNote(true);
    let note = formatEmployeeNote(noteValue);
    addEmployeeNote(
      note,
      internetUserId,
      (newNote) => {
        if (newNote) {
          setIsAddingNote(false);
          setNotes([newNote, ...notes]);
        }
      },
      mounted,
    );
  };

  const notesFormatted = notes
    .filter(
      (n) => filterOnFilterDate(filterDate, n.createdDateUtc) && (!selectedCategory || n.category === selectedCategory),
    )
    .map((n) => ({
      dateCreated: n.createdDateUtc,
      dateUpdated: n.dateUpdated,
      body: n.body,
      createdByUserDisplayName: n.createdByUserDisplayName,
      category: n.category,
    }));

  const employeeNotesColumns = [
    { name: "Date", dataSource: formatNoteDateCreated },
    { name: "Details", dataSource: "body", style: { minWidth: "250px" }, className: "team-details-body" },
    { name: "Performed By", dataSource: "createdByUserDisplayName" },
    { name: "Type", dataSource: "category" },
  ];

  const trainingNotesFields = [];

  if (canAddNotes && !hideAddNotes) {
    trainingNotesFields.push({
      control:
        canAddNotes &&
        (isAddingNote ? (
          <MSpinner />
        ) : (
          <Box sx={{ marginTop: 1 }}>
            <NoteInputField saveNote={addNote} placeholder="Add Internal Note" />
          </Box>
        )),
    });
  }
  if (canViewNotes) {
    trainingNotesFields.push({ control: getTabbedHistoryTable(employeeNotesColumns, notesFormatted, "Notes", "id") });
  }

  return isFetchingNotes ? (
    <MSpinner />
  ) : (
    <MCard
      title="TEAM"
      isCollapsible={isCollapsible}
      startCollapsed={isCollapsible}
      cellWidth={!overrideCellWidth && (cellWidth || 12)}
      isEditable={false}
      disableHoverStyles
      fields={trainingNotesFields}
      className="training-notes-card"
      toolTipTitle="Training, Education, Accountability, Management"
      headerContent={
        <MSelect
          className="category-type-dropdown"
          isClearable
          placeholder="Filter by Type"
          options={employeeNoteCategories.map((category) => ({
            value: category,
            label: category,
          }))}
          onChange={(selectedOption) => setSelectedCategory(selectedOption?.value ?? null)}
        />
      }
    />
  );
};

EmployeeTEAM.propTypes = {
  internetUserId: PropTypes.number.isRequired,
  isCollapsible: PropTypes.bool,
  cellWidth: PropTypes.number,
  canViewNotes: PropTypes.bool,
  canAddNotes: PropTypes.bool,
  hideAddNotes: PropTypes.bool,
  overrideCellWidth: PropTypes.bool, //in case you don't want any cell width
  filterDate: PropTypes.instanceOf(Date),
};

const mapStateToProps = (state) => {
  return {
    canViewNotes: userHasRight(EMPLOYEENOTES_VIEW)(state),
    canAddNotes: userHasRight(EMPLOYEENOTES_ADD)(state),
  };
};

export default connect(mapStateToProps)(EmployeeTEAM);
